import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/components/ui/popover';
import { kluczPodpowiedziPigulki, kluczPolaZapisu, type StanPanelu } from '@/lib/mojeFiltry';
import { CZAS_DYMKA_CENY_MS } from '@/components/search/ZakladkaMojichFiltrow';

/**
 * „MOJE FILTRY” W PANELU FILTRÓW (build plan C2, plan E1.4, §1.4, §1.71; makiety
 * `serp-saved-filters-proto*.html` — panel, i `serp-filter-panel.html` — nagłówek i stopka).
 *
 * Obie kontrolki działają na SZKICU panelu, jak każda inna zmiana w nim: wchodzą dopiero z
 * „Pokaż N ofert”. Reguły stanu: `stanPanelu` w `src/lib/mojeFiltry.ts`.
 */

/**
 * Pastylka w nagłówku panelu. Zapalona, gdy szkic = Moje filtry; dotknięcie zapalonej czyści szkic,
 * zgaszonej — wczytuje zbiór. Bez zapisanego zbioru wyszarzona z „Nie masz jeszcze zapisanych
 * filtrów” (hover na pulpicie; na dotyku ten sam tekst w dymku po dotknięciu — nigdy cisza).
 */
export function PastylkaMoichFiltrow({ stan, opis, naKlik }: { stan: StanPanelu; opis: string; naKlik: () => void }) {
  const { t } = useTranslation();
  const [dymek, setDymek] = useState(false);
  const czasomierz = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(czasomierz.current), []);
  const tekst = t(kluczPodpowiedziPigulki(stan), { set: opis });

  const klik = () => {
    if (stan.pigulkaAktywna) { naKlik(); return; }
    setDymek(true);
    window.clearTimeout(czasomierz.current);
    czasomierz.current = window.setTimeout(() => setDymek(false), CZAS_DYMKA_CENY_MS);
  };

  return (
    <Popover open={dymek} onOpenChange={setDymek}>
      <Tooltip>
        <PopoverAnchor asChild>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={klik}
              data-testid="pastylka-moich-filtrow"
              data-zapalona={stan.pigulkaZapalona ? 'tak' : 'nie'}
              aria-pressed={stan.pigulkaAktywna ? stan.pigulkaZapalona : undefined}
              // aria-disabled, nie disabled: wyłączony przycisk nie dostaje hovera, a podpowiedź jest treścią (§1.71).
              aria-disabled={stan.pigulkaAktywna ? undefined : true}
              className={cn(
                'strefa-dotyku relative inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border pl-3.5 pr-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                stan.pigulkaZapalona
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-foreground',
                stan.pigulkaAktywna ? !stan.pigulkaZapalona && 'hover:bg-muted' : 'cursor-default opacity-50',
              )}
            >
              <Bookmark className={cn('h-[18px] w-[18px]', stan.pigulkaZapalona ? 'fill-current' : 'text-muted-foreground')} aria-hidden />
              {t('search.my_filters.name')}
            </button>
          </TooltipTrigger>
        </PopoverAnchor>
        <TooltipContent side="bottom" align="end" data-testid="podpowiedz-pastylki">{tekst}</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        data-testid="dymek-pastylki"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="w-auto max-w-[calc(100vw-2rem)] rounded-xl border-0 bg-foreground px-3 py-1.5 text-xs text-background shadow-md"
      >
        {tekst}
        <PopoverArrow className="fill-foreground" />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Pole „Zapisz zmiany w moich filtrach” / „Zapisz jako moje filtry” nad „Pokaż N ofert”. Widać je,
 * gdy szkic różni się od Moich filtrów (przed pierwszym zapisem zawsze); domyślnie odznaczone; bez
 * ceny w szkicu wyszarzone z „Wybierz cenę, żeby zapisać.” pod spodem i samo się odznacza.
 * Zaznaczone + „Pokaż N ofert” = to samo co „Zapisz” przy fladze.
 */
export function PoleZapisuMoichFiltrow({ stan, maZbior, naZmiane }: { stan: StanPanelu; maZbior: boolean; naZmiane: (v: boolean) => void }) {
  const { t } = useTranslation();
  if (!stan.pokazPole) return null;
  const wylaczone = !stan.poleAktywne;
  return (
    <div data-testid="pole-zapisu-moich-filtrow" className="border-t pt-3">
      <button
        type="button"
        role="checkbox"
        aria-checked={stan.zaznaczone}
        aria-disabled={wylaczone || undefined}
        aria-describedby={wylaczone ? 'pole-zapisu-dlaczego' : undefined}
        onClick={() => { if (!wylaczone) naZmiane(!stan.zaznaczone); }}
        className={cn(
          'strefa-dotyku relative flex items-center gap-2.5 text-left text-base font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm',
          wylaczone && 'cursor-default opacity-60',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border',
            stan.zaznaczone ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background',
          )}
        >
          {stan.zaznaczone && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
        {t(kluczPolaZapisu(maZbior))}
        <Bookmark className="h-4 w-4 text-muted-foreground" aria-hidden />
      </button>
      {wylaczone && (
        <p id="pole-zapisu-dlaczego" className="ml-7 mt-0.5 text-xs text-muted-foreground">
          {t('search.my_filters.need_price_save')}
        </p>
      )}
    </div>
  );
}
